/* eslint-disable no-restricted-syntax */
/* eslint-disable complexity */
import {Locale} from '@aboutyou/config';
import {getStaticRoute_UNSAFE} from './getStaticRoute';
import {
  getPageComponentKeysByLocale_UNSAFE,
  getStaticPagesByLocale_UNSAFE,
  LEGAL_PAGE_COMPONENT,
} from './legal-pages';
import {LEGAL_PAGE_TYPE} from './legalPagesRouteKeys';
import {RouteKey} from './routeKeys';
import {getSpecialCategoriesByLocale_UNSAFE} from './specialCategories';
import {getSpecialSlugsByLocale_UNSAFE} from './specialSlugs';
import {getTopCategoryIdsByLocale_UNSAFE} from './topCategoryIds';
import {getTopCategorySlugsByLocale_UNSAFE} from './topCategorySlugs';

enum GenericRoutes {
  browse = '/browse',
  privacyPolicy = '/privacy-policy',
  termsAndConditions = '/terms-and-conditions',
  generalConditions = '/general-conditions',
  imprint = '/imprint',
  aboutUs = '/about-us',
  contactWithdrawal = '/contract-withdrawal',
  cookieList = '/cookie-list',
  faq = '/faq',
  faqContact = '/faq/contact',
  faqShippingAndDeliveryCosts = '/faq/shipping-and-delivery-costs',
  faqPayment = '/faq/payment',
  voucherTermsAndConditions = '/voucher-terms-and-conditions',
  campaignTermsAndConditions = '/campaign-terms-and-conditions',
  basket = '/basket',
  wishlist = '/wishlist',
  search = '/search',
  termsOfUse = '/terms-of-use',
  termsOfUseReviews = '/terms-of-use-and-guidelines-for-customer-reviews',
  yourShop = '/your-shop',
  women = '/women',
  womenSale = '/women/sale',
  womenClothesNew = '/women/clothes/new',
  womenBrand = '/women/brand',
  womenSport = '/women/sport',
  womenOutfits = '/women/outfits',
  men = '/men',
  menSale = '/men/sale',
  menClothesNew = '/men/clothes/new',
  menBrand = '/men/brand',
  menSport = '/men/sport',
  menOutfits = '/men/outfits',
  kids = '/kids',
}

/**
 * Returns the generic urls (in english) which all stacks have to handle for every locale
 */
export const getGenericUrls_UNSAFE = () => {
  return Object.values(GenericRoutes);
};

/**
 * Map international entry paths to localized ones (if available)
 *
 * e.g. `/women/clothes` would be mapped to `/frauen/bekleidung` in `de_DE`
 *
 * @param url
 * @param targetLocale
 */
export function resolveGenericUrl_UNSAFE(
  url: string,
  targetLocale: Locale,
): string | null {
  switch (url) {
    case GenericRoutes.browse:
      return '/';

    case GenericRoutes.privacyPolicy: {
      const staticPages = getStaticPagesByLocale_UNSAFE(targetLocale);
      return staticPages[LEGAL_PAGE_TYPE.Privacy]
        ? `/${staticPages[LEGAL_PAGE_TYPE.Privacy]}`
        : null;
    }

    case GenericRoutes.termsAndConditions: {
      const staticPages = getStaticPagesByLocale_UNSAFE(targetLocale);
      return staticPages[LEGAL_PAGE_TYPE.TermsAndConditions]
        ? `/${staticPages[LEGAL_PAGE_TYPE.TermsAndConditions]}`
        : null;
    }

    // `/general-conditions` is the canonical T&C slug only for English-based
    // locales; for every other locale it must redirect to the localized T&C
    // URL (e.g. `/agb` for de_DE). Self-redirects for the English-based
    // locales are filtered out in getRedirectsMap_UNSAFE.
    case GenericRoutes.generalConditions: {
      const staticPages = getStaticPagesByLocale_UNSAFE(targetLocale);
      return staticPages[LEGAL_PAGE_TYPE.TermsAndConditions]
        ? `/${staticPages[LEGAL_PAGE_TYPE.TermsAndConditions]}`
        : null;
    }

    case GenericRoutes.imprint: {
      const staticPages = getStaticPagesByLocale_UNSAFE(targetLocale);
      return staticPages[LEGAL_PAGE_TYPE.Imprint]
        ? `/${staticPages[LEGAL_PAGE_TYPE.Imprint]}`
        : null;
    }

    case GenericRoutes.aboutUs:
      return `/${
        getPageComponentKeysByLocale_UNSAFE(targetLocale)[
          LEGAL_PAGE_COMPONENT.AboutUs
        ]
      }`;

    case GenericRoutes.contactWithdrawal: {
      const staticPages = getStaticPagesByLocale_UNSAFE(targetLocale);
      return staticPages[LEGAL_PAGE_TYPE.TermsAndConditions]
        ? `/${staticPages[LEGAL_PAGE_TYPE.TermsAndConditions]}#${
            getSpecialSlugsByLocale_UNSAFE(targetLocale).cancellation
          }`
        : null;
    }

    case GenericRoutes.cookieList: {
      const staticPages = getStaticPagesByLocale_UNSAFE(targetLocale);
      return staticPages[LEGAL_PAGE_TYPE.Cookies]
        ? `/${staticPages[LEGAL_PAGE_TYPE.Cookies]}`
        : null;
    }

    case GenericRoutes.faq: {
      const staticPages = getStaticPagesByLocale_UNSAFE(targetLocale);
      return staticPages[LEGAL_PAGE_TYPE.FAQ]
        ? `/${staticPages[LEGAL_PAGE_TYPE.FAQ]}`
        : null;
    }

    case GenericRoutes.faqContact: {
      const staticPages = getStaticPagesByLocale_UNSAFE(targetLocale);
      return staticPages[LEGAL_PAGE_TYPE.FAQ]
        ? `/${staticPages[LEGAL_PAGE_TYPE.FAQ]}/${
            getSpecialSlugsByLocale_UNSAFE(targetLocale).contact
          }`
        : null;
    }

    case GenericRoutes.faqShippingAndDeliveryCosts: {
      const staticPages = getStaticPagesByLocale_UNSAFE(targetLocale);
      return staticPages[LEGAL_PAGE_TYPE.FAQ]
        ? `/${staticPages[LEGAL_PAGE_TYPE.FAQ]}/${
            getSpecialSlugsByLocale_UNSAFE(targetLocale).shippingAndDelivery
          }`
        : null;
    }

    case GenericRoutes.faqPayment: {
      const staticPages = getStaticPagesByLocale_UNSAFE(targetLocale);
      return staticPages[LEGAL_PAGE_TYPE.FAQ]
        ? `/${staticPages[LEGAL_PAGE_TYPE.FAQ]}/${
            getSpecialSlugsByLocale_UNSAFE(targetLocale).payment
          }`
        : null;
    }

    case GenericRoutes.voucherTermsAndConditions: {
      return GenericRoutes.voucherTermsAndConditions;
    }

    case GenericRoutes.campaignTermsAndConditions:
      return `/${
        getPageComponentKeysByLocale_UNSAFE(targetLocale).campaignConditions
      }`;

    case GenericRoutes.basket:
      return getStaticRoute_UNSAFE(RouteKey.Basket, targetLocale);

    case GenericRoutes.wishlist:
      return getStaticRoute_UNSAFE(RouteKey.Wishlist, targetLocale);

    case GenericRoutes.termsOfUse: {
      const staticPages = getStaticPagesByLocale_UNSAFE(targetLocale);
      return staticPages[LEGAL_PAGE_TYPE.TermsOfUse]
        ? `/${staticPages[LEGAL_PAGE_TYPE.TermsOfUse]}`
        : null;
    }

    case GenericRoutes.termsOfUseReviews: {
      const staticPages = getStaticPagesByLocale_UNSAFE(targetLocale);
      return staticPages[LEGAL_PAGE_TYPE.TermsOfUseReviews]
        ? `/${staticPages[LEGAL_PAGE_TYPE.TermsOfUseReviews]}`
        : null;
    }

    case GenericRoutes.yourShop:
      return getStaticRoute_UNSAFE(RouteKey.YourShop, targetLocale);

    case GenericRoutes.women: {
      const topCategorySlugConfig =
        getTopCategorySlugsByLocale_UNSAFE(targetLocale);
      return `/${topCategorySlugConfig.female}`;
    }

    case GenericRoutes.womenSale: {
      const specialCategorySlugs =
        getSpecialCategoriesByLocale_UNSAFE(targetLocale);
      const topCategorySlugConfig =
        getTopCategorySlugsByLocale_UNSAFE(targetLocale);
      return `/${topCategorySlugConfig.female}/${specialCategorySlugs.sale}`;
    }

    case GenericRoutes.womenClothesNew: {
      const specialCategorySlugs =
        getSpecialCategoriesByLocale_UNSAFE(targetLocale);
      const topCategorySlugConfig =
        getTopCategorySlugsByLocale_UNSAFE(targetLocale);
      return `/${topCategorySlugConfig.female}/${specialCategorySlugs.clothing}/${specialCategorySlugs.new}`;
    }

    case GenericRoutes.womenBrand: {
      const categoryIds = getTopCategoryIdsByLocale_UNSAFE(targetLocale);
      return `${getStaticRoute_UNSAFE(
        RouteKey.Brands,
        targetLocale,
      )}?category=${categoryIds.female}`;
    }

    case GenericRoutes.womenSport: {
      const specialCategorySlugs =
        getSpecialCategoriesByLocale_UNSAFE(targetLocale);
      const topCategorySlugConfig =
        getTopCategorySlugsByLocale_UNSAFE(targetLocale);
      return `/${topCategorySlugConfig.female}/${specialCategorySlugs.sport}`;
    }

    case GenericRoutes.womenOutfits: {
      const specialCategorySlugs =
        getSpecialCategoriesByLocale_UNSAFE(targetLocale);
      const topCategorySlugConfig =
        getTopCategorySlugsByLocale_UNSAFE(targetLocale);
      return `/${topCategorySlugConfig.female}/${specialCategorySlugs.outfits}`;
    }

    case GenericRoutes.men: {
      const topCategorySlugConfig =
        getTopCategorySlugsByLocale_UNSAFE(targetLocale);
      return `/${topCategorySlugConfig.male}`;
    }

    case GenericRoutes.menSale: {
      const specialCategorySlugs =
        getSpecialCategoriesByLocale_UNSAFE(targetLocale);
      const topCategorySlugConfig =
        getTopCategorySlugsByLocale_UNSAFE(targetLocale);
      return `/${topCategorySlugConfig.male}/${specialCategorySlugs.sale}`;
    }

    case GenericRoutes.menClothesNew: {
      const specialCategorySlugs =
        getSpecialCategoriesByLocale_UNSAFE(targetLocale);
      const topCategorySlugConfig =
        getTopCategorySlugsByLocale_UNSAFE(targetLocale);
      return `/${topCategorySlugConfig.male}/${specialCategorySlugs.clothing}/${specialCategorySlugs.new}`;
    }

    case GenericRoutes.menBrand: {
      const categoryIds = getTopCategoryIdsByLocale_UNSAFE(targetLocale);
      return `${getStaticRoute_UNSAFE(
        RouteKey.Brands,
        targetLocale,
      )}?category=${categoryIds.male}`;
    }

    case GenericRoutes.menSport: {
      const specialCategorySlugs =
        getSpecialCategoriesByLocale_UNSAFE(targetLocale);
      const topCategorySlugConfig =
        getTopCategorySlugsByLocale_UNSAFE(targetLocale);
      return `/${topCategorySlugConfig.male}/${specialCategorySlugs.sport}`;
    }

    case GenericRoutes.menOutfits: {
      const specialCategorySlugs =
        getSpecialCategoriesByLocale_UNSAFE(targetLocale);
      const topCategorySlugConfig =
        getTopCategorySlugsByLocale_UNSAFE(targetLocale);
      return `/${topCategorySlugConfig.male}/${specialCategorySlugs.outfits}`;
    }

    case GenericRoutes.kids: {
      const topCategorySlugConfig =
        getTopCategorySlugsByLocale_UNSAFE(targetLocale);
      return `/${topCategorySlugConfig.children}`;
    }

    default:
      return null;
  }
}
